/* eslint-disable no-nested-ternary */
import React, { FC } from 'react';
import clsx from 'clsx';
import { CardsTypes, TypeContentfulMap } from 'Constants/variables';
import Skillbuilder from 'components/Skillbuilder';
import TestimonialCard from 'components/Testimonial';
import Button from '../Button';
import Carousel from '../Carousel';
import ChallengeCard from '../FeaturedChallenge';
import { ICarouselWrapperProps } from './@types';
import ImageComponent from '../Image';
import LeftArrow from '../../../public/static/images/homePage/carouselBlade/left_arrow.svg';
import RightArrow from '../../../public/static/images/homePage/carouselBlade/right_arrow.svg';
import LeftArrowBlue from '../../../public/static/images/homePage/carouselBlade/left_arrow_blue.svg';
import RightArrowBlue from '../../../public/static/images/homePage/carouselBlade/right_arrow_blue.svg';
import MediaNonFlipCard from '../MediaCards/MediaNonFlipCard';
import SectionWrapper from '../SectionWrapper';
import Typo from '../Typo';
import WinnerCard from '../WinnerCard';
import styles from './carouselwrapper.module.sass';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';

export const SliderArrow = React.forwardRef<
  HTMLButtonElement,
  {
    type: string;
    onClick: React.MouseEventHandler<HTMLSpanElement> | undefined;
    isCentered?: boolean;
    alternateArrow?: string;
    alternateTheme?: boolean;
  }
>((props, ref) => {
  const { type, onClick, isCentered, alternateArrow, alternateTheme } = props;
  const className = type === 'next' ? styles.nextArrow : styles.prevArrow;

  return (
    <button
      type="button"
      ref={ref}
      className={clsx(
        'arrow',
        className,
        styles.arrow,
        isCentered && alternateTheme ? styles.alternateTheme : '',
        isCentered ? styles.centeredArrow : type === 'next' ? 'nextArrow' : 'prevArrow',
      )}
      onClick={onClick}
      aria-hidden="true"
    >
      <ImageComponent
        width={12}
        image={alternateArrow || (type === 'next' ? RightArrow : LeftArrow)}
      />
    </button>
  );
});

const CarouselWrapper: FC<ICarouselWrapperProps> = ({
  cards,
  type,
  title,
  subtitle,
  description,
  topLink,
  bottomLink,
  hasCenteredArrows,
  backgroundColor,
}) => {
  const { isMobile } = useIsMobileViewport();

  const getSettings = () => {
    switch (type) {
      case CardsTypes.WINNER:
      case CardsTypes.SKILLBUILDER:
      case CardsTypes.CHALLENGE:
      case CardsTypes.IMAGE:
        return {
          dots: true,
          infinite: true,
          speed: 250,
          arrows: true,
          slidesToScroll: 1,
          variableWidth: true,
          centerMode: true,
          slidesToShow: 1,
          nextArrow: (
            <SliderArrow type="next" onClick={this} isCentered={isMobile || hasCenteredArrows} />
          ),
          prevArrow: (
            <SliderArrow type="prev" onClick={this} isCentered={isMobile || hasCenteredArrows} />
          ),
        };
      case CardsTypes.TESTIMONIAL:
        return {
          slidesToScroll: 1,
          speed: 500,
          centerMode: true,
          variableWidth: true,
          dots: true,
          arrows: true,
          infinite: true,
          slidesToShow: 3,
          nextArrow: (
            <SliderArrow
              type="next"
              alternateArrow={RightArrowBlue}
              onClick={this}
              alternateTheme
              isCentered={isMobile || hasCenteredArrows}
            />
          ),
          prevArrow: (
            <SliderArrow
              type="prev"
              alternateArrow={LeftArrowBlue}
              alternateTheme
              onClick={this}
              isCentered={isMobile || hasCenteredArrows}
            />
          ),
        };
      case CardsTypes.PARTNER:
        return {
          slidesToScroll: 1,
          speed: 500,
          autoplay: true,
          centerMode: true,
          variableWidth: true,
          dots: false,
          arrows: false,
          infinite: true,
          slidesToShow: isMobile ? 3 : 6,
        };
      default:
        return {};
    }
  };
  let slideWidth = 0;
  let headingClass;
  switch (type) {
    case CardsTypes.PARTNER:
      slideWidth = 106;
      headingClass = 'is-color-Black';
      break;
    case CardsTypes.WINNER:
      slideWidth = isMobile ? 270 : 924;
      headingClass = `is-color-HighLightLime ${styles.winner}`;
      break;
    case CardsTypes.TESTIMONIAL:
      headingClass = 'is-color-Black';
      slideWidth = isMobile ? 270 : 390;
      break;
    case CardsTypes.CHALLENGE:
      slideWidth = isMobile ? 270 : 457;
      headingClass = 'is-color-White';
      break;
    case CardsTypes.IMAGE:
      slideWidth = isMobile ? 270 : 475;
      headingClass = 'is-color-Black';
      break;
    case CardsTypes.SKILLBUILDER:
      slideWidth = isMobile ? 270 : 924;
      headingClass = 'is-color-Black';
      break;
    default:
      break;
  }

  const cardsNotMatchingType = cards.filter(
    card => card.sys.contentType.sys.id !== TypeContentfulMap[type],
  );

  if (cardsNotMatchingType.length) {
    const notMatchingCards = cardsNotMatchingType
      .map((card: any) => card.fields.friendlyName)
      .join('\n - ');
    throw new Error(
      `Error in component: ${title}.\nSome cards are not of type ${type},\nCards not matching the type are: \n - ${notMatchingCards}`,
    );
  }

  return (
    <SectionWrapper
      classes={styles.root}
      backgroundColor={backgroundColor}
      hasBodyMargin={type === CardsTypes.PARTNER}
    >
      <div>
        <div className={clsx(headingClass)}>
          {title && (
            <div className={`is-flex is-justify-content-center has-text-centered mb-2`}>
              <Typo classes={styles.title}>{title}</Typo>
            </div>
          )}
          {subtitle && (
            <div className={`is-flex is-justify-content-center mb-2`}>
              <Typo classes={styles.subtitle}>{subtitle}</Typo>
            </div>
          )}
          {description && (
            <Typo
              classes={`${styles.description} mb-2 is-flex is-justify-content-center has-text-centered`}
            >
              {description}
            </Typo>
          )}
          {topLink && (
            <div
              className={`${styles.topLink} is-flex is-justify-content-${
                isMobile ? 'center' : 'flex-end'
              } is-align-items-center`}
            >
              <Button
                isBtn={false}
                link={topLink}
                icon="arrowWhiteRight"
                classes={`is-color-White ${styles.anchorTag}`}
                iconClass="ml-3"
              />
            </div>
          )}
        </div>
        <Carousel
          slideWidth={slideWidth}
          settings={getSettings()}
          blurInactive={type !== CardsTypes.PARTNER && type !== CardsTypes.WINNER}
        >
          {cards.map(card => {
            switch (type) {
              case CardsTypes.PARTNER:
                return (
                  <ImageComponent
                    key={card.sys.id}
                    image={card.fields.image}
                    width={140}
                    height={70}
                    classes={styles.partnerImage}
                  />
                );
              case CardsTypes.WINNER:
                return <WinnerCard key={card.sys.id} {...card.fields} />;
              case CardsTypes.CHALLENGE:
                return <ChallengeCard key={card.sys.id} {...card} />;
              case CardsTypes.TESTIMONIAL:
                return <TestimonialCard key={card.sys.id} {...card.fields} />;
              case CardsTypes.IMAGE:
                return (
                  <MediaNonFlipCard
                    key={card.sys.id}
                    {...{
                      ...card,
                      descColor: 'is-color-Black',
                      inCarousel: true,
                    }}
                  />
                );
              case CardsTypes.SKILLBUILDER:
                return <Skillbuilder key={card.sys.id} {...card} />;
              default:
                return <div />;
            }
          })}
        </Carousel>
        {bottomLink && (
          <div className={`${styles.bottomLink} is-flex is-justify-content-center`}>
            <Button link={bottomLink} />
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

export default CarouselWrapper;
